import React from "react";
import { CARE_GUIDE_DIET_TRAIT_LABELS } from "../utils/careGuideQuickFacts";
import { filtersToUrlParams, FILTER_PARAM_NAMES } from "../utils/urlParamUtils";

/**
 * Component that displays active filters as clickable badges.
 * Clicking an individual badge removes that filter.
 */
const ActiveFiltersIndicator = ({
  filters,
  onClearFilters,
  onRemoveFilter,
  defaultValues,
  speciesMap = {},
  storesData = {},
  isQuerying = false,
  storesInitialized = true,
  reefWizardTanks = [],
  extraFilters = [],
}) => {
  const formatNumericValue = (value, decimals = 0) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    if (decimals === 0 || Number.isInteger(numericValue)) {
      return String(numericValue);
    }

    return numericValue
      .toFixed(decimals)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1");
  };

  const titleCaseValueList = (values) =>
    values
      .map((value) => value.charAt(0).toUpperCase() + value.slice(1))
      .join(", ");

  const getActiveFilters = () => {
    const activeFilters = [];
    const urlParams = filtersToUrlParams(
      filters,
      defaultValues,
      true,
      storesInitialized
    );

    if (filters.sortOrder) {
      activeFilters.push({
        key: "sortOrder",
        label: "Sort",
        value: filters.sortOrder,
        displayValue: filters.sortOrder
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (value) => value.toUpperCase()),
      });
    }

    for (const [paramName, paramValue] of urlParams.entries()) {
      switch (paramName) {
        case FILTER_PARAM_NAMES.SEARCH:
          activeFilters.push({
            key: "searchTerm",
            label: "Search",
            value: paramValue,
            displayValue: `"${paramValue}"`,
          });
          break;

        case FILTER_PARAM_NAMES.SCIENTIFIC_NAME: {
          const scientificName = decodeURIComponent(paramValue);
          const commonName = speciesMap[scientificName];
          activeFilters.push({
            key: "scientificFilter",
            label: "Species",
            value: scientificName,
            displayValue: commonName
              ? `${scientificName} (${commonName})`
              : scientificName,
          });
          break;
        }

        case FILTER_PARAM_NAMES.CATEGORY:
          activeFilters.push({
            key: "categoryFilter",
            label: "Category",
            value: paramValue,
            displayValue: paramValue,
          });
          break;

        case FILTER_PARAM_NAMES.GENUS:
          activeFilters.push({
            key: "genusFilter",
            label: "Genus",
            value: decodeURIComponent(paramValue),
            displayValue: decodeURIComponent(paramValue),
          });
          break;

        case FILTER_PARAM_NAMES.FAMILY:
          activeFilters.push({
            key: "familyFilter",
            label: "Family",
            value: decodeURIComponent(paramValue),
            displayValue: decodeURIComponent(paramValue),
          });
          break;

        case FILTER_PARAM_NAMES.ORDER:
          activeFilters.push({
            key: "orderFilter",
            label: "Order",
            value: decodeURIComponent(paramValue),
            displayValue: decodeURIComponent(paramValue),
          });
          break;

        case FILTER_PARAM_NAMES.CLASS:
          activeFilters.push({
            key: "classFilter",
            label: "Class",
            value: decodeURIComponent(paramValue),
            displayValue: decodeURIComponent(paramValue),
          });
          break;

        case FILTER_PARAM_NAMES.PHYLUM:
          activeFilters.push({
            key: "phylumFilter",
            label: "Phylum",
            value: decodeURIComponent(paramValue),
            displayValue: decodeURIComponent(paramValue),
          });
          break;

        case FILTER_PARAM_NAMES.FRESHWATER:
        case FILTER_PARAM_NAMES.SALTWATER: {
          const freshwater =
            urlParams.get(FILTER_PARAM_NAMES.FRESHWATER) !== "false";
          const saltwater =
            urlParams.get(FILTER_PARAM_NAMES.SALTWATER) !== "false";

          if (!freshwater || !saltwater) {
            if (!activeFilters.find((filter) => filter.key === "waterType")) {
              let displayValue = "None";
              if (freshwater && !saltwater) {
                displayValue = "Freshwater only";
              } else if (!freshwater && saltwater) {
                displayValue = "Saltwater only";
              }

              activeFilters.push({
                key: "waterType",
                label: "Water Type",
                value: displayValue,
                displayValue,
              });
            }
          }
          break;
        }

        case FILTER_PARAM_NAMES.STORES: {
          const storeUrls = paramValue?.split(",") || [];
          const availableStoreUrls = defaultValues.allowedStores || [];
          const includedStores = [
            ...new Set(
              storeUrls.filter((url) => availableStoreUrls.includes(url))
            ),
          ];
          const excludedStores = [
            ...new Set(
              availableStoreUrls.filter((url) => !storeUrls.includes(url))
            ),
          ];

          let displayValue;
          if (
            includedStores.length > 0 &&
            includedStores.length < [...new Set(availableStoreUrls)].length
          ) {
            if (includedStores.length > excludedStores.length) {
              displayValue = `All except ${excludedStores
                .map((storeUrl) => storesData[storeUrl] || storeUrl)
                .join(", ")}`;
            } else {
              displayValue = includedStores
                .map((storeUrl) => storesData[storeUrl] || storeUrl)
                .join(", ");
            }
          }

          if (includedStores.length === 0 && excludedStores.length > 0) {
            displayValue = "None";
          }

          activeFilters.push({
            key: "allowedStores",
            label: "Stores",
            value: displayValue,
            displayValue,
          });
          break;
        }

        case FILTER_PARAM_NAMES.FAVORITES_ONLY:
          if (paramValue === "true") {
            activeFilters.push({
              key: "favoritesOnly",
              label: "Favorites",
              value: "Favorites only",
              displayValue: "",
            });
          }
          break;

        case FILTER_PARAM_NAMES.NOTIFICATIONS_ONLY:
          if (paramValue === "true") {
            activeFilters.push({
              key: "notificationsOnly",
              label: "Notifications",
              value: "Notifications only",
              displayValue: "",
            });
          }
          break;

        case FILTER_PARAM_NAMES.REEF_WIZARD_ONLY:
          if (paramValue === "true") {
            const tankId = urlParams.get(FILTER_PARAM_NAMES.REEF_WIZARD_TANK);
            const tank = tankId
              ? reefWizardTanks.find((entry) => entry.id === tankId)
              : null;

            activeFilters.push({
              key: "reefWizardOnly",
              label: "Reef Wizard",
              value: "Recommendations only",
              displayValue: tank ? tank.name : "",
            });
          }
          break;

        case FILTER_PARAM_NAMES.REEF_WIZARD_TANK:
          break;

        case FILTER_PARAM_NAMES.SIZE_MIN:
        case FILTER_PARAM_NAMES.SIZE_MAX:
        case FILTER_PARAM_NAMES.INCLUDE_NUMERIC_SIZES:
        case FILTER_PARAM_NAMES.INCLUDE_NON_NUMERIC_SIZES:
        case FILTER_PARAM_NAMES.NON_NUMERIC_SIZES:
          if (!activeFilters.find((filter) => filter.key === "sizeFilter")) {
            const minSize = defaultValues.minSize ?? 0;
            const maxSize = defaultValues.maxSize ?? 36;
            const sizeMin = filters.sizeFilter?.[0] ?? minSize;
            const sizeMax = filters.sizeFilter?.[1] ?? maxSize;
            const includeNumeric = filters.includeNumericSizes !== false;
            const includeNonNumeric = filters.includeNonNumericSizes !== false;
            const sizeParts = [];

            if (sizeMin > minSize || sizeMax < maxSize) {
              if (includeNumeric) {
                sizeParts.push(`${sizeMin}"-${sizeMax}"`);
              }
            }

            if (!includeNumeric) {
              sizeParts.push("No numeric sizes");
            }

            if (filters.nonNumericSizeFilter) {
              const allNonNumericSizes =
                defaultValues.availableNonNumericSizes || [];
              const includedSizes = [
                ...new Set(
                  filters.nonNumericSizeFilter.filter((value) =>
                    allNonNumericSizes.includes(value)
                  )
                ),
              ];
              const excludedSizes = [
                ...new Set(
                  allNonNumericSizes.filter(
                    (value) => !includedSizes.includes(value)
                  )
                ),
              ];

              if (
                includedSizes.length > 0 &&
                includedSizes.length < [...new Set(allNonNumericSizes)].length
              ) {
                if (includedSizes.length > excludedSizes.length) {
                  sizeParts.push(
                    `All non-numeric sizes except ${excludedSizes.join(", ")}`
                  );
                } else {
                  sizeParts.push(includedSizes.join(", "));
                }
              }
            }

            if (!includeNonNumeric) {
              sizeParts.push("No non-numeric sizes");
            }

            if (sizeParts.length > 0) {
              activeFilters.push({
                key: "sizeFilter",
                label: "Size",
                value: sizeParts.join(" | "),
                displayValue: sizeParts.join(" | "),
              });
            }
          }
          break;

        case FILTER_PARAM_NAMES.MIN_TANK_SIZE_MAX:
          activeFilters.push({
            key: "careRequirements",
            label: "Max Tank Size",
            value: paramValue,
            displayValue: `<= ${paramValue} gal`,
          });
          break;

        case FILTER_PARAM_NAMES.MAX_SIZE_MAX:
          activeFilters.push({
            key: "careRequirements",
            label: "Max Size",
            value: paramValue,
            displayValue: `<= ${formatNumericValue(paramValue, 1)}"`,
          });
          break;

        case FILTER_PARAM_NAMES.TEMPERATURE_MIN:
        case FILTER_PARAM_NAMES.TEMPERATURE_MAX:
          if (!activeFilters.find((filter) => filter.label === "Temperature")) {
            const min = urlParams.get(FILTER_PARAM_NAMES.TEMPERATURE_MIN);
            const max = urlParams.get(FILTER_PARAM_NAMES.TEMPERATURE_MAX);
            if (min && max) {
              activeFilters.push({
                key: "careRequirements",
                label: "Temperature",
                value: `${min}-${max}`,
                displayValue: `${min}-${max} F`,
              });
            }
          }
          break;

        case FILTER_PARAM_NAMES.PH_MIN:
        case FILTER_PARAM_NAMES.PH_MAX:
          if (!activeFilters.find((filter) => filter.label === "pH")) {
            const min = urlParams.get(FILTER_PARAM_NAMES.PH_MIN);
            const max = urlParams.get(FILTER_PARAM_NAMES.PH_MAX);
            if (min && max) {
              activeFilters.push({
                key: "careRequirements",
                label: "pH",
                value: `${min}-${max}`,
                displayValue: `${formatNumericValue(min, 1)}-${formatNumericValue(
                  max,
                  1
                )}`,
              });
            }
          }
          break;

        case FILTER_PARAM_NAMES.CARE_LEVELS: {
          const levels = paramValue.split(",");
          activeFilters.push({
            key: "careRequirements",
            label: "Care Level",
            value: paramValue,
            displayValue: titleCaseValueList(levels),
          });
          break;
        }

        case FILTER_PARAM_NAMES.REEF_SAFE: {
          const values = paramValue.split(",");
          activeFilters.push({
            key: "careRequirements",
            label: "Reef Safe",
            value: paramValue,
            displayValue: titleCaseValueList(values),
          });
          break;
        }

        case FILTER_PARAM_NAMES.PLANT_SAFE: {
          const values = paramValue.split(",");
          activeFilters.push({
            key: "careRequirements",
            label: "Plant Safe",
            value: paramValue,
            displayValue: titleCaseValueList(values),
          });
          break;
        }

        case FILTER_PARAM_NAMES.TEMPERAMENT: {
          const values = paramValue.split(",");
          activeFilters.push({
            key: "careRequirements",
            label: "Temperament",
            value: paramValue,
            displayValue: titleCaseValueList(values),
          });
          break;
        }

        case FILTER_PARAM_NAMES.DIET_TRAITS: {
          const values = paramValue.split(",");
          activeFilters.push({
            key: "careRequirements",
            label: "Diet",
            value: paramValue,
            displayValue: values
              .map((value) => CARE_GUIDE_DIET_TRAIT_LABELS[value] || value)
              .join(", "),
          });
          break;
        }

        case FILTER_PARAM_NAMES.LIFESPAN_MIN:
        case FILTER_PARAM_NAMES.LIFESPAN_MAX:
          if (!activeFilters.find((filter) => filter.label === "Lifespan")) {
            const min = urlParams.get(FILTER_PARAM_NAMES.LIFESPAN_MIN);
            const max = urlParams.get(FILTER_PARAM_NAMES.LIFESPAN_MAX);
            if (min && max) {
              activeFilters.push({
                key: "careRequirements",
                label: "Lifespan",
                value: `${min}-${max}`,
                displayValue: `${formatNumericValue(min, 1)}-${formatNumericValue(
                  max,
                  1
                )} years`,
              });
            }
          }
          break;

        case FILTER_PARAM_NAMES.HAS_CARE_GUIDE:
          activeFilters.push({
            key: "careRequirements",
            label: "Care Guide",
            value: paramValue,
            displayValue:
              paramValue === "yes" ? "Has care guide" : "No care guide",
          });
          break;

        default:
          break;
      }
    }

    return activeFilters;
  };

  const activeFilters = [...getActiveFilters(), ...extraFilters];

  if (activeFilters.length === 0) {
    return null;
  }

  const handleRemoveFilter = (filterKey) => {
    if (onRemoveFilter) {
      onRemoveFilter(filterKey);
    }
  };

  return (
    <div className="active-filters-indicator p-2 bg-blue-300 border border-blue-500 rounded-b-lg">
      <div className="flex flex-wrap gap-2">
        {isQuerying && (
          <div className="inline-flex items-center bg-blue-50 border border-blue-400 rounded-full px-3 py-1 text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-blue-600 font-medium">
              Updating results...
            </span>
          </div>
        )}
        {activeFilters.map((filter, index) => (
          <div
            key={`${filter.key}-${filter.label}-${index}`}
            className="inline-flex items-center bg-white border border-blue-400 rounded-full px-3 py-1 text-sm cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => {
              if (filter.key === "sortOrder") {
                return;
              }
              handleRemoveFilter(filter.key);
            }}
            title={
              filter.key !== "sortOrder"
                ? `Click to remove ${filter.label} filter`
                : ""
            }
          >
            {filter.displayValue ? (
              <>
                <span className="font-semibold text-blue-700 mr-1">
                  {filter.label}:
                </span>
                <span className="text-gray-700 mr-2">
                  {filter.displayValue}
                </span>
              </>
            ) : (
              <span className="font-semibold text-blue-700 mr-1">
                {filter.label}
              </span>
            )}

            {filter.key !== "sortOrder" && (
              <span className="text-blue-600 font-bold">x</span>
            )}
          </div>
        ))}
        {activeFilters.filter((filter) => filter.key !== "sortOrder").length >
          0 && (
          <button
            className="inline-flex items-center bg-white border border-red-400 rounded-full px-3 py-1 text-sm text-red-700 hover:bg-red-100 transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              onClearFilters();
            }}
            title="Click to clear all filters"
          >
            Clear All Filters x
          </button>
        )}
      </div>
    </div>
  );
};

export default ActiveFiltersIndicator;
